<template>
  <div class="animated fadeIn">
    <loader :isVisible="loading" />

    <ClientsProspectsMapFilters
      type="prospect"
      :initial-filter="initialFilter"
      @applyFilters="applyFilters"
    />

    <tabs v-model="activeTab" v-if="!loading && prospects">
      <tab header="Départements">
        <StatsFranceMap :data="mapData" stats="prospects" color-scheme="blue" />
      </tab>
      <tab header="Marqueurs">
        <ClientProspectMarkersMap
          :data="prospects"
          :filters="filters"
          type="prospects"
        />
      </tab>
    </tabs>
  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters } from "vuex";

import Loader from "@/components/utils/Loader";
import Tabs from "vue-strap/src/Tabs";
import Tab from "vue-strap/src/Tab";
import ClientsProspectsMapFilters from "@/components/client/ClientsProspectsMapFilters";
import StatsFranceMap from "@/components/stats/StatsFranceMap";
import ClientProspectMarkersMap from "@/components/client/ClientProspectMarkersMap";

export default {
  components: {
    Loader,
    Tabs,
    Tab,
    ClientsProspectsMapFilters,
    StatsFranceMap,
    ClientProspectMarkersMap,
  },

  data() {
    return {
      loading: false,
      activeTab: 0,
      prospects: null,
      filters: {
        begin: null,
        end: null,
        status: [],
      },
      initialFilter: {},
    };
  },

  computed: {
    ...mapState(["concepts"]),
    ...mapGetters({
      departments: "getDepartments",
      prospectStatus: "getProspectStatus",
    }),

    displayedDepartments() {
      const domTom = ["971", "972", "973", "974", "976"];
      const metroDepartments = this.departments.filter(
        (dpt) => !domTom.includes(dpt.code) && dpt.code !== "all",
      );

      return metroDepartments.map(({ code, name }) => {
        if (code === "all") {
          return { label: name, value: code };
        } else {
          return { label: code + " - " + name, value: code };
        }
      });
    },

    mapData() {
      const defaultData = {
        total: 0,
        totalPercentage: 0,
        reasons: [],
      };

      const departments = [
        ...this.displayedDepartments.map((dpt) => ({
          ...dpt,
          data: defaultData,
        })),
      ];

      if (!this.loading && this.prospects) {
        departments.forEach((dpt) => {
          dpt.data = this.getDepartmentProspectInfo(dpt.value);
        });
      }

      return departments;
    },
  },

  async created() {
    if (
      this.$route.query.begin ||
      this.$route.query.end ||
      this.$route.query.status
    ) {
      this.filters = {
        begin: this.$route.query.begin
          ? moment(this.$route.query.begin).toDate()
          : undefined,
        end: this.$route.query.end
          ? moment(this.$route.query.end).toDate()
          : undefined,
        status: this.$route.query.status
          ? Array.isArray(this.$route.query.status)
            ? this.$route.query.status
            : [this.$route.query.status]
          : [],
      };

      this.initialFilter = { ...this.filters };
      this.loadProspects();
    } else {
      this.initialFilter = { ...this.filters };
    }
  },

  methods: {
    async loadProspects() {
      try {
        this.loading = true;

        const { data } = await this.$api.get("/prospects/map", {
          params: {
            begin: this.filters.begin,
            end: this.filters.end,
            status: this.filters.status,
          },
        });

        this.prospects = data;
      } catch (e) {
        this.prospects = [];

        this.$errorToast("Impossible de charger les prospects");
      } finally {
        this.loading = false;
      }
    },

    getDepartmentProspectInfo(dpt) {
      const departmentProspects = this.prospects.filter((prospect) => {
        if (
          prospect.zipcode &&
          typeof prospect.zipcode === "string" &&
          prospect.geoloc
        ) {
          return prospect.zipcode.slice(0, 2) === dpt;
        }

        return false;
      });

      const total = departmentProspects.length;
      let totalPercentage = (total / this.prospects.length) * 100;

      if (isNaN(totalPercentage)) {
        totalPercentage = 0;
      }

      const statuses = [];

      for (const prospect of departmentProspects) {
        const status = statuses.find(
          ({ value }) => value === prospect.prospect.type,
        );

        if (status) {
          status.total = (status.total || 0) + 1;
        } else {
          statuses.push({
            value: prospect.prospect.type,
            label: this.getStatusLabel(prospect.prospect.type),
            color: this.getStatusColor(prospect.prospect.type),
            total: 1,
          });
        }
      }

      for (const status of statuses) {
        let percentage = (status.total / total) * 100;

        if (isNaN(percentage)) {
          percentage = 0;
        }

        status.percentage = percentage.toFixed(2);
        status.name = `${status.label} (${status.total}) (${status.percentage}%)`;
      }

      return { total, totalPercentage, reasons: statuses };
    },

    getStatusLabel(status) {
      const statusObj = this.prospectStatus.find((s) => s.value === status);

      return statusObj ? statusObj.label : status;
    },

    getStatusColor(status) {
      const statusObj = this.prospectStatus.find((s) => s.value === status);

      return statusObj ? statusObj.color : "#95a5a6";
    },

    applyFilters(form) {
      this.filters = {
        ...this.filters,
        ...form,
      };

      this.loadProspects();
    },
  },
};
</script>
